/* ═══════════════════════════════════════════════════
   TrackLab v2 — Analytics
   Batched event tracking via navigator.sendBeacon.
   ═══════════════════════════════════════════════════ */

(function () {
  'use strict';

  TL.analytics = {};

  var ENDPOINT = '/api/track/';
  var FLUSH_INTERVAL = 10000;   // flush every 10 seconds
  var FLUSH_THRESHOLD = 10;     // or when queue hits 10 events
  var IMMEDIATE_TYPES = { clickout: true };  // bypass queue, send now

  var _queue = [];
  var _flushTimer = null;
  var _visitorId = null;
  var _sessionId = null;

  // ── Visitor ID (persistent across sessions, localStorage) ──
  function _getVisitorId() {
    if (_visitorId) return _visitorId;
    try {
      _visitorId = localStorage.getItem('tl_vid');
      if (!_visitorId) {
        _visitorId = _uuid4();
        localStorage.setItem('tl_vid', _visitorId);
      }
    } catch (e) {
      _visitorId = '';  // private browsing or storage full
    }
    return _visitorId;
  }

  // ── Session ID (per browser session, sessionStorage) ──
  function _getSessionId() {
    if (_sessionId) return _sessionId;
    try {
      _sessionId = sessionStorage.getItem('tl_sid');
      if (!_sessionId) {
        _sessionId = _uuid4();
        sessionStorage.setItem('tl_sid', _sessionId);
      }
    } catch (e) {
      _sessionId = '';  // private browsing or storage full
    }
    return _sessionId;
  }

  function _uuid4() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = Math.random() * 16 | 0;
      return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
    });
  }

  // ── Send payload to server ──
  function _send(events) {
    var payload = JSON.stringify({ events: events });
    if (navigator.sendBeacon) {
      navigator.sendBeacon(ENDPOINT, new Blob([payload], { type: 'application/json' }));
    } else {
      fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: payload,
        keepalive: true
      }).catch(function () {});
    }
  }

  // ── Flush queue to server ──
  function _flush() {
    if (!_queue.length) return;
    _send(_queue.splice(0));
  }

  // ── Start periodic flush timer ──
  function _startTimer() {
    if (_flushTimer) return;
    _flushTimer = setInterval(_flush, FLUSH_INTERVAL);
  }

  // ── Core track ──
  TL.analytics.track = function (type, data) {
    var event = {
      type: type,
      page: location.pathname,
      visitor_id: _getVisitorId(),
      session_id: _getSessionId(),
      data: data || {}
    };

    if (IMMEDIATE_TYPES[type]) {
      _send([event]);
      return;
    }

    _queue.push(event);
    _startTimer();

    if (_queue.length >= FLUSH_THRESHOLD) _flush();
  };

  // ── Flush on page hide (tab switch, navigation, close) ──
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'hidden') _flush();
  });
  window.addEventListener('pagehide', _flush);

  // ── Helpers ──
  function _findRaceEl(raceId) {
    return document.querySelector('.live-banner[data-race-id="' + raceId + '"]')
        || document.querySelector('.race-row[data-race-id="' + raceId + '"]')
        || document.querySelector('.result-card[data-race-id="' + raceId + '"]');
  }

  function _eventId(raceId) {
    var el = _findRaceEl(raceId);
    return el ? (el.dataset.eventId || '') : '';
  }

  // ── Click-out tracking ──
  TL.analytics.trackClickout = function () {
    var sel = null;
    if (TL.state.activeRaceId) {
      sel = document.querySelector('#v2Expanded-' + TL.state.activeRaceId + ' .runner.sel')
         || document.querySelector('#v2InlineDetail-' + TL.state.activeRaceId + ' .runner.sel');
    }

    TL.analytics.track('clickout', {
      race_id: TL.state.activeRaceId || '',
      event_id: _eventId(TL.state.activeRaceId),
      horse: sel ? (sel.dataset.horse || '') : '',
      bookmaker: TL.state.bestBookmaker || '',
      odds: (TL.state.betNum && TL.state.betDen) ? (TL.state.betNum + '/' + TL.state.betDen) : '',
      score: sel ? (Math.round(Number(sel.dataset.score)) || 0) : 0,
      stake: TL.state.betAmt || 0,
      value_signal: sel ? (sel.dataset.valueSignal || '') : ''
    });
  };

  // ── Race expand tracking ──
  TL.analytics.trackRaceExpand = function (raceId, source) {
    var el = _findRaceEl(raceId);

    TL.analytics.track('race_expand', {
      race_id: raceId || '',
      event_id: el ? (el.dataset.eventId || '') : '',
      venue: el ? (el.dataset.venue || '') : '',
      source: source || ''
    });
  };

  // ── Runner select tracking ──
  TL.analytics.trackRunnerSelect = function (el, raceId) {
    if (!el) return;
    var runners = el.parentElement ? el.parentElement.querySelectorAll('.runner') : [];
    var rank = 0;
    for (var i = 0; i < runners.length; i++) {
      if (runners[i] === el) { rank = i + 1; break; }
    }

    TL.analytics.track('runner_select', {
      race_id: raceId || '',
      event_id: _eventId(raceId),
      horse: el.dataset.horse || '',
      score: Math.round(Number(el.dataset.score)) || 0,
      odds: el.dataset.odds || '',
      rank: rank
    });
  };

  // ── Result expand tracking ──
  TL.analytics.trackResultExpand = function (card) {
    if (!card) return;
    TL.analytics.track('result_expand', {
      race_id: card.dataset.raceId || '',
      event_id: card.dataset.eventId || '',
      venue: card.dataset.venue || ''
    });
  };

  // ── Page view tracking ──
  TL.analytics.trackPageView = function () {
    var data = {
      referrer: document.referrer || '',
      title: document.title || ''
    };

    // Navigation Timing API — real user performance metrics
    var nav = performance && performance.getEntriesByType
      ? performance.getEntriesByType('navigation')[0]
      : null;
    if (nav) {
      data.ttfb = Math.round(nav.responseStart - nav.startTime);
      data.dom_ready = Math.round(nav.domContentLoadedEventEnd - nav.startTime);
      data.load_time = Math.round(nav.loadEventEnd - nav.startTime);
    }

    // Viewport & device metadata
    data.vw = window.innerWidth || 0;
    data.vh = window.innerHeight || 0;
    data.dpr = window.devicePixelRatio || 1;
    data.touch = 'ontouchstart' in window;

    TL.analytics.track('pageview', data);
  };

  // Auto-track after page load so timing data is available
  if (document.readyState === 'complete') {
    TL.analytics.trackPageView();
  } else {
    window.addEventListener('load', function () {
      // Small delay to ensure loadEventEnd is populated
      setTimeout(TL.analytics.trackPageView, 0);
    });
  }
})();
